import { Trello } from "types/my-trello-types";
import { initTrelloIframeConsumerClient } from "../iframe/iframeClient/iframe.api";
import { TrelloIframeMockFactory } from "../iframe/iframeClient/iframeMock.api";
import { isTest } from "src/js/modal/utils/environment-provider";
import { isBrowser, isInIframe } from "@v2/shared/lib/runtime";

type ClientListener = () => void;

export class TrelloIframeClientManager {
  private static cachedClient: Trello.PowerUp.IFrame | null = null;
  private static listeners = new Set<ClientListener>();
  private static context: "initializer" | "consumer" | undefined = undefined;

  static markAsInitializerContext() {
    if (!isBrowser()) return;
    this.context = "initializer";
  }
  static markAsConsumerContext() {
    if (!isBrowser()) return;
    this.context = "consumer";
  }

  static getClient(): Trello.PowerUp.IFrame | undefined {
    if (this.cachedClient) {
      return this.cachedClient;
    }

    if (!isInIframe() || isTest()) {
      this.cachedClient = TrelloIframeMockFactory.create();
      this.notifySubscribers();
      return this.cachedClient;
    }

    if (!this.context) {
      // AIDEV-NOTE: consumer iframe mounted before initializer; Trello API unavailable
      console.warn(
        "Trello client unavailable; initializer context not established yet"
      );
      return undefined;
    }

    if (this.context === "initializer") {
      return undefined;
    }

    try {
      // we are in the consumer context (not in the connector page)
      return this.reauthorizeClient();
    } catch {
      return undefined;
    }
  }

  static reauthorizeClient() {
    // AIDEV-NOTE: Separate from the getClient(), because the popup with Trello authorization will redirect to a standalone (non-iframe) page.
    // In that scenario the getClient() would return a mock, and we need the real client.
    this.cachedClient = initTrelloIframeConsumerClient();
    this.notifySubscribers();
    return this.cachedClient;
  }

  static subscribeToClientUpdates(listener: ClientListener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  static async waitForClient(): Promise<Trello.PowerUp.IFrame> {
    const existingClient = this.getClient();
    if (existingClient) {
      return existingClient;
    }

    return new Promise((resolve) => {
      const unsubscribe = this.subscribeToClientUpdates(() => {
        const readyClient = this.getClient();
        if (readyClient) {
          unsubscribe();
          resolve(readyClient);
        }
      });
    });
  }

  private static notifySubscribers() {
    this.listeners.forEach((listener) => listener());
  }
}
